import {
  handleSubmitMultilingualForm,
  useCreateContact,
  useGetContactFormData,
  useUpdateContact,
} from "@/api/cms.ts";
import { ContactForm } from "@/components/cms/ContactForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema, contactContentSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  contactContentSchema
    .omit({ id: true })
    .merge(cmsPageSchema.pick({ pageName: true, published: true })),
);

type FormData = z.infer<typeof formSchema>;

const ContactCMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const { data: contactData } = useGetContactFormData();
  const create = useCreateContact();
  const update = useUpdateContact();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const contact = contactData?.[lang];
        return {
          ...contact?.content,
          published: contact?.published ?? false,
          pageName: contact?.pageName ?? "",
        };
      }),
    [contactData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = contactData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const { pageName, published, ...content } = localeData;

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("contactSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("contactErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout pageName={t("cmsTabContact")} formIds={["contact"]}>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmitMultilingualForm(form, contactData, onSubmit)}
          id="contact"
          className="flex w-full gap-4"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <ContactForm language={lang} />}
          />
        </form>
      </FormProvider>
    </CMSLayout>
  );
};

export default ContactCMS;
